import React from "react";
import { ProductFeaturesHero } from "components/hero";
import { LottieWithStaticSafariImage } from "ui/lottie";
import heroImage from "../../../../public/assets/solutions/healthcare/static-hero.png";
import firstMobileHeroImage from "../../../../public/assets/solutions/healthcare/hero-mobile-1.png";
import secondMobileHeroImage from "../../../../public/assets/solutions/healthcare/hero-mobile-2.png";
import thirdMobileHeroImage from "../../../../public/assets/solutions/healthcare/hero-mobile-3.png";
import animationData from "./hero-animation.json";

type HealthcareHeroProps = {
  eyebrow: string;
  title: string;
  description: string;
};

export function HealthcareHero({ eyebrow, title, description }: HealthcareHeroProps) {
  return (
    <ProductFeaturesHero
      theme="dark"
      variant="green"
      eyebrow={eyebrow}
      title={title}
      description={description}
      titleMaxW="740px"
      media={
        <LottieWithStaticSafariImage
          isPriority
          ratio={1320 / 553}
          animationData={animationData}
          image={heroImage}
          alt="Illustration depicting data flowing left-to-right between three columns. The first column highlights trusted and compliant data such as website interactions, claim information, and in-office records. The second column represents secure data usage, highlighting various audiences like prospective users, users due for visit, and users with a new claim. All audiences have lock icons around them depicting secure access rules. The third column shows secure PHI data flowing into activated destinations like targeting accounts in Meta, sending emails via Iterable, and notifying billing teams through alerts in slack."
          containerProps={{
            borderRadius: "bigger",
            overflow: "hidden",
            py: "tiniest", // Padding ensures the border radius has no hard edges
            bgGradient: "linear(to-b, white -60%, #F2F8F7 40%)",
          }}
        />
      }
      featureGridProps={{ pb: { base: "huge", lg: "small" } }}
      productFeatures={[
        {
          id: "feature-1",
          title: "Collect trusted and compliant data",
          description:
            "Establish proactive data contracts so that Hightouch only collects approved, standardized data. Ensure that your data is high quality and highly vetted, before you ever store it.",
          cta: {
            label: "Learn more about data contracts",
            href: "/blog/data-contracts-hightouch-events",
            variant: "primary",
            target: "_blank",
          },
          mobileImage: {
            src: firstMobileHeroImage,
            alt: "Illustration representing trusted and compliant data such as website interactions, claim information, and in-office records.",
          },
        },
        {
          id: "feature-2",
          title: "Govern how your data gets used",
          description:
            "Set robust rules to manage which users can access which data, and where and how that data can get used. Ensure compliance from this data layer at the center of all of your other tools.",
          cta: {
            label: "Learn more about data governance",
            href: "/platform/reverse-etl#data-governance",
            variant: "primary",
            target: "_blank",
          },
          mobileImage: {
            src: secondMobileHeroImage,
            alt: "Illustration representing secure data usage. Highlighting various audiences like prospective users, users due for visit, and users with a new claim. All audiences have lock icons around them depicting secure access rules.",
          },
        },
        {
          id: "feature-3",
          title: "Activate PHI data securely",
          description:
            "Safely sync PHI data from your warehouse to HIPAA-compliant destinations. Give your marketing teams self-service access to compliant marketing data.",
          cta: {
            label: "Learn how Hightouch supports marketing",
            href: "/solutions/marketing",
            variant: "primary",
            target: "_blank",
          },
          mobileImage: {
            src: thirdMobileHeroImage,
            alt: "Illustration representing secure PHI data flowing into activated destinations like targeting accounts in Meta, sending emails via Iterable, and notifying billing teams through alerts in slack.",
          },
        },
      ]}
    />
  );
}
